import React, { useState } from "react";
import { Card, CardBody, CardHeader } from "reactstrap";

const GrowthChart = ({ data = [], ticketMedio = 0, activeMembers = 0 }) => {
    const [hoveredIdx, setHoveredIdx] = useState(null);

    // Relationship: Active Members (Bars) vs Ticket Médio (Line)
    const rawData = data.length > 0 ? data : [];
    const hasHistory = rawData.some(d => d.currentActive > 0 || d.prevActive > 0 || d.ticket > 0);
    
    // safeData fica vazio para que a UI de "Coletando dados" seja exibida e os fallback de ativos funcionem
    const safeData = hasHistory ? rawData : [];
    
    // Fallback: If no historical data yet, use current values
    const maxActive = Math.max(10, activeMembers, ...safeData.map(d => Math.max(d.currentActive || 0, d.prevActive || 0))) * 1.2;
    const maxTicket = Math.max(50, ticketMedio, ...safeData.map(d => d.ticket || 0)) * 1.2;

    const formatBRL = (val) => new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(val);

    const currentActiveTotal = hasHistory ? safeData[safeData.length - 1].currentActive : activeMembers;
    const prevActiveTotal = hasHistory ? safeData[safeData.length - 1].prevActive : 0;
    
    const activeEvol = prevActiveTotal > 0 ? Math.round(((currentActiveTotal - prevActiveTotal) / prevActiveTotal) * 100) : 0;

    // Build Catmull-Rom smooth spline logic
    const ticketPoints = safeData.map((item, idx) => {
        // x is exactly in the middle of each bar column
        // Each column width is (100 / safeData.length)%
        // So center is (100 / safeData.length) * (idx + 0.5)
        return {
            x: (100 / Math.max(1, safeData.length)) * (idx + 0.5),
            y: 100 - (item.ticket / maxTicket) * 100
        };
    });

    const buildSmoothPath = (pts) => {
        if (pts.length === 0) return "";
        if (pts.length === 1) return `M ${pts[0].x} ${pts[0].y}`;
        const tension = 0.3;

        let path = `M ${pts[0].x} ${pts[0].y}`;

        for (let i = 0; i < pts.length - 1; i++) {
            const p0 = pts[Math.max(0, i - 1)];
            const p1 = pts[i];
            const p2 = pts[i + 1];
            const p3 = pts[Math.min(pts.length - 1, i + 2)];

            const cp1x = p1.x + (p2.x - p0.x) * tension;
            const cp1y = p1.y + (p2.y - p0.y) * tension;
            const cp2x = p2.x - (p3.x - p1.x) * tension;
            const cp2y = p2.y - (p3.y - p1.y) * tension;

            path += ` C ${cp1x} ${cp1y}, ${cp2x} ${cp2y}, ${p2.x} ${p2.y}`;
        }

        return path;
    };

    const ticketSmoothPath = buildSmoothPath(ticketPoints);

    return (
        <Card className="border-0 shadow-sm h-100" style={{ borderRadius: "4px" }}>
            <CardHeader className="bg-white border-0 pt-4 px-4 pb-0">
                <div className="d-flex justify-content-between align-items-center mb-4">
                    <h5 className="fw-bold m-0 text-primary" style={{ fontSize: "18px" }}>Crescimento</h5>
                    <div className="d-flex align-items-center gap-4">
                        <div className="text-end">
                            <small className="text-muted d-block fw-bold font-size-11">Ativos</small>
                            <span className="fw-bold font-size-18 text-dark">{currentActiveTotal}</span>
                        </div>
                        <div className="text-end">
                            <small className="text-muted d-block fw-bold font-size-11">Ticket M.</small>
                            <span className="fw-bold font-size-18 text-dark">{formatBRL(ticketMedio)}</span>
                        </div>
                        <div className="text-end">
                            <small className="text-muted d-block fw-bold font-size-11">Evol.</small>
                            <span className={`fw-bold font-size-18 ${activeEvol >= 0 ? 'text-success' : 'text-danger'}`}>
                                {activeEvol > 0 ? '+' : ''}{activeEvol}%
                            </span>
                        </div>
                    </div>
                </div>
            </CardHeader>
            <CardBody className="p-4 pt-1">
                <div style={{ height: "320px", position: "relative", paddingLeft: "10px", paddingRight: "10px" }}>
                    {/* Legend - Moved inside to save vertical space */}
                    <div className="d-flex gap-3 justify-content-center w-100" style={{ position: "absolute", top: "-15px", left: 0, zIndex: 10 }}>
                        <div className="d-flex align-items-center gap-1">
                            <span className="rounded-circle" style={{ width: "8px", height: "8px", backgroundColor: "#E9F2FF" }} />
                            <span className="text-muted font-size-10 fw-bold">Ano Ant.</span>
                        </div>
                        <div className="d-flex align-items-center gap-1">
                            <span className="rounded-circle" style={{ width: "8px", height: "8px", backgroundColor: "#A0C4FF" }} />
                            <span className="text-muted font-size-10 fw-bold">Ano Atual</span>
                        </div>
                        <div className="d-flex align-items-center gap-1">
                            <span style={{ width: "12px", height: "3px", backgroundColor: "var(--bs-primary)", borderRadius: "2px" }} />
                            <span className="text-muted font-size-10 fw-bold">Ticket</span>
                        </div>
                    </div>

                    {safeData.length === 0 ? (
                        <div className="w-100 h-100 d-flex flex-column align-items-center justify-content-center text-muted">
                            <i className="mdi mdi-chart-box-outline font-size-24 mb-2 opacity-50"></i>
                            <span className="font-size-12 fw-bold opacity-75">Coletando primeio registro histórico...</span>
                        </div>
                    ) : (
                        <div className="h-100 w-100 d-flex position-relative align-items-end" style={{ zIndex: 1, paddingBottom: "35px", gap: "10px" }}>
                            
                            {/* O SVG Interativo para Linhas e Curvas */}
                            {safeData.length > 0 && (
                                <svg 
                                    style={{ 
                                        position: "absolute", 
                                        top: 0, 
                                        left: 0, 
                                        width: "100%", 
                                        height: "calc(100% - 35px)", 
                                        pointerEvents: "none",
                                        zIndex: 4 
                                    }}
                                    preserveAspectRatio="none"
                                    viewBox="0 0 100 100"
                                >
                                    <defs>
                                        <filter id="ticketShadow" x="-20%" y="-20%" width="140%" height="140%">
                                            <feDropShadow dx="0" dy="4" stdDeviation="5" floodColor="var(--bs-primary)" floodOpacity="0.5" />
                                        </filter>
                                    </defs>
                                    <path
                                        d={ticketSmoothPath}
                                        fill="none"
                                        stroke="var(--bs-primary)"
                                        strokeWidth="3.5"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        vectorEffect="non-scaling-stroke"
                                        filter="url(#ticketShadow)"
                                    />
                                </svg>
                            )}
                            
                            {/* Interatividade visual principal de colunas */}
                            {safeData.map((item, idx) => {
                                const barHeightCurrent = (item.currentActive / maxActive) * 100;
                                const barHeightPrev = (item.prevActive / maxActive) * 100;
                                const isHovered = hoveredIdx === idx;
                            
                            return (
                                <div 
                                    key={idx} 
                                    className="flex-grow-1 h-100 d-flex flex-column align-items-center justify-content-end position-relative"
                                    onMouseEnter={() => setHoveredIdx(idx)}
                                    onMouseLeave={() => setHoveredIdx(null)}
                                    style={{ cursor: "crosshair" }}
                                >
                                {/* Overlay / Fundo escurecido atrás do mês Hoverado (Guia vertical visual) */}
                                {isHovered && (
                                    <div 
                                        className="position-absolute w-100 rounded-3 shadow-sm"
                                        style={{ height: "calc(100% + 10px)", top: "-5px", backgroundColor: "rgba(243, 244, 246, 0.5)", zIndex: 0 }}
                                    ></div>
                                )}

                                {/* Ponto Real-Time sobre a Linha Suave do Ticket */}
                                {isHovered && ticketPoints[idx] && (
                                    <div
                                        className="position-absolute"
                                        style={{
                                            bottom: `calc(${100 - ticketPoints[idx].y}% - 0%)`,
                                            width: "14px",
                                            height: "14px",
                                            borderRadius: "50%",
                                            left: "50%",
                                            backgroundColor: "#fff",
                                            border: `3px solid var(--bs-primary)`,
                                            transform: "translate(-50%, 50%)",
                                            zIndex: 20,
                                            boxShadow: "0 0 0 4px rgba(85, 110, 230, 0.2), 0 2px 4px rgba(0,0,0,0.15)"
                                        }}
                                    />
                                )}

                                {/* Card de Tooltip Suspenso */}
                                {isHovered && (
                                    <div 
                                        className="position-absolute bg-white shadow-lg rounded-3 p-3 text-start"
                                        style={{ 
                                            bottom: Math.max(barHeightCurrent, barHeightPrev) + 15 + "%", 
                                            width: "180px",
                                            left: "50%", 
                                            transform: "translate(-50%, 0)",
                                            pointerEvents: "none",
                                            zIndex: 9999,
                                            border: "1px solid #e5e7eb"
                                        }}
                                    >
                                        <h6 className="fw-bold mb-3 pb-2 border-bottom text-center text-primary text-uppercase font-size-12">{item.month}</h6>
                                        <div className="d-flex justify-content-between align-items-center mb-2">
                                            <span className="text-muted small d-flex align-items-center">
                                                <span className="rounded-circle shadow-sm me-2" style={{ width: "8px", height: "8px", backgroundColor: "#A0C4FF" }} />
                                                Ativos Ano
                                            </span>
                                            <span className="fw-bold font-size-13 text-dark">{item.currentActive}</span>
                                        </div>
                                        <div className="d-flex justify-content-between align-items-center mb-3">
                                            <span className="text-muted small d-flex align-items-center">
                                                <span className="rounded-circle shadow-sm me-2" style={{ width: "8px", height: "8px", backgroundColor: "#E9F2FF" }} />
                                                Ano Ant.
                                            </span>
                                            <span className="fw-bold font-size-13 text-secondary">{item.prevActive}</span>
                                        </div>
                                        <div className="d-flex justify-content-between pt-2 mt-2 border-top align-items-center">
                                            <span className="text-muted small d-flex align-items-center">
                                                <span className="me-2 rounded-1 shadow-sm" style={{ width: "10px", height: "3px", backgroundColor: "var(--bs-primary)" }} />
                                                Ticket M.
                                            </span>
                                            <span className="fw-bold font-size-13 text-primary">
                                                {formatBRL(item.ticket)}
                                            </span>
                                        </div>
                                    </div>
                                )}

                                {/* Comparison Bars */}
                                <div className="d-flex align-items-end gap-1 w-100 h-100" style={{ padding: "0 10%", zIndex: 1, pointerEvents: "none" }}>
                                    <div 
                                        className="flex-grow-1"
                                        style={{ 
                                            height: `${barHeightPrev}%`, 
                                            backgroundColor: "#E9F2FF", 
                                            borderRadius: "2px 2px 0 0",
                                            transition: "height 0.4s ease-out"
                                        }} 
                                    />
                                    <div 
                                        className="flex-grow-1"
                                        style={{ 
                                            height: `${barHeightCurrent}%`, 
                                            backgroundColor: "#A0C4FF", 
                                            borderRadius: "2px 2px 0 0",
                                            transition: "height 0.4s ease-out"
                                        }} 
                                    />
                                </div>

                                <span className={`position-absolute text-muted fw-bold transition-all ${isHovered ? 'text-primary' : ''}`} style={{ bottom: "-25px", fontSize: "10px" }}>{item.month}</span>
                            </div>
                        );
                    })}
                        </div>
                    )}
                </div>
                
                <div className="mt-4 text-center">
                    <span 
                        className="text-muted text-uppercase fw-bold" 
                        style={{ fontSize: "11px", letterSpacing: "1px", opacity: 0.7 }}
                    >
                        Performance: Alunos vs Ticket (12 meses)
                    </span>
                </div>
            </CardBody>
        </Card>
    );
};

export default GrowthChart;
